/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { 
  useGetSyncOptionQuery, 
  useUpdateSyncOptionMutation,
  useGetSshCliAccessQuery,
  useUpdateSshCliAccessMutation
} from "../../../../store/api/admin/adminApiSlice";
import { ISyncOptions } from "../../../../store/api/admin/types";
import { setOpenToast } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { errorMessage } from "../../../../utils/helpers";

export const useSyncOptionData = () => {
  const dispatch = useStoreDispatch();
  const { data, refetch } = useGetSyncOptionQuery();
  const [updateSyncOption, syncOptionStatus] = useUpdateSyncOptionMutation();
  
  const { data: sshCliData, refetch: refetchSshCli } = useGetSshCliAccessQuery();
  const [updateSshCliAccess, sshCliStatus] = useUpdateSshCliAccessMutation();

  const [formData, setFormData] = useState<ISyncOptions>({
    cmEnabled: false,
    dbEnabled: false,
    exportType: "",
  });
  
  const [sshCliEnabled, setSshCliEnabled] = useState<boolean>(true); // Default to enabled

  useEffect(() => {
    !!data && setFormData(data);
  }, [data]);
  
  useEffect(() => {
    if (sshCliData !== undefined) {
      setSshCliEnabled(sshCliData.enabled);
    }
  }, [sshCliData]);

  const submitHandler = async () => {
    try {
      let result: any;

      result = await updateSyncOption(formData);

      if (!!result?.error) throw result?.error;

      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `The Sync Option has been successfully updated.`,
        })
      );
      refetch();
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };
  
  const submitSshCliHandler = async () => {
    try {
      let result: any;

      result = await updateSshCliAccess({ enabled: sshCliEnabled });

      if (!!result?.error) throw result?.error;

      const statusText = sshCliEnabled ? "enabled" : "disabled";
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `SSH CLI access has been ${statusText}. Active SSH sessions will remain unaffected until reconnection.`,
        })
      );
      refetchSshCli();
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    formData,
    setFormData,
    syncOptionStatus,
    submitHandler,
    sshCliEnabled,
    setSshCliEnabled,
    sshCliStatus,
    submitSshCliHandler,
  };
};
